import { projectUploadObservation } from "@liveswitch-contact/types";
import Dexie from "dexie";
import { adminUploadTelemetry } from "./adminUploadTelemetry";
import {
	CHUNK_SIZE,
	planKnownSizeUploadSource,
	type UploadKnownSizeControl,
	type UploadsAnyCreateRequest,
	selectSmallFileObjectControl,
	readKnownSizeUploadReceipt,
	readUploadKnownSizeControl,
	isEvidenceTerminalFailure,
	uploadControlError,
	receiptConfirmationPendingError,
	type UploadsFacadeClient,
	FINALIZING_RECEIPT_STATUSES,
	classifyParkedSmallFileRow,
	describeParkedPayload,
	computePoisonPacedUntil,
	createBlobPutTransport,
	classifyUploadFailure,
	isReceiptConfirmationPendingError,
	isRegistryNotFoundError,
	exceedsMultipartPartLimit,
	maxUploadableBytes,
	newUploadId,
	putToS3WithStallTimeout,
	isUploadTerminalFailure,
	readUploadRefusalTrigger,
	readUploadTerminalDetails,
	tagUploadRefusalTrigger,
	readUploadFacadeErrorCode,
	requiredMultipartParts,
	SmallFileUploadLane,
	validateUploadTargetEnvelope,
	withSchedulerLease,
	type SmallFileCompletionReceiptCheckpoint,
	type SmallFilePresignTarget,
	type SmallFileReceiptState,
	type SmallFileReceiptQuery,
	type SmallFileWire,
	type SmallFileRecord,
	type SmallFileStore,
	type SmallFileUploadClaim,
	type DurableLeaseToken,
	type BlobPutFetch,
	type UploadTargetEnvelope,
	type UploadsFacadeTransport
} from "@uploads";
import constants from "@/api/constants";
import { logError } from "@/helpers/logError";
import { db, readIdbErrorEvidence, withIdbReopen, type AdminSmallFileUpload } from "@/db/fileUploadDb";
import { getAdminDurableOwnershipForCoordinator } from "./adminUploadLease";
import { ensureAdminUploadBridgeReady } from "./adminUploadMigrationIntegration";
import { redactedUploadFailure, uploadFailureLogDetails } from "./uploadLogRedaction";

export type AdminSmallFileOperation = "snapshot" | "annotation" | "rotation" | "logo" | "virtual-background";

export interface AdminSmallFileApi {
	get?(url: string): Promise<any>;
	_uploadsFacade(): UploadsFacadeClient;
	createUploadsUpload: (body: Record<string, unknown>, signal?: AbortSignal) => Promise<unknown>;
	getUploadsUpload: (registryId: string) => Promise<unknown>;
	completeUploadsUpload?: (registryId: string, attemptId: string, generation: number, parts: unknown[]) => Promise<unknown>;
	getUploadsObjectStatus?: (registryId: string, attemptId: string, generation: number) => Promise<boolean>;
	abortUploadsUpload: (
		registryId: string,
		attemptId: string,
		generation: number,
		transport: UploadsFacadeTransport,
		takeGeneration?: string,
		preserveOwner?: boolean
	) => Promise<unknown>;
}

export interface AdminSmallFileUploadRequest {
	id: string;
	conversationId?: string;
	organizationId?: string;
	objectControl?: UploadKnownSizeControl;
	blob: Blob;
	operation: AdminSmallFileOperation;
	snapshotId?: string;
	parentId?: string | null;
	idempotencyId?: string;
	metadata?: Record<string, unknown>;
	signal?: AbortSignal;
}

export interface AdminSmallFileUploadResult {
	mediaUrl?: string;
	organization?: { id: string; logoUrl?: string; virtualBackgroundImageUrl?: string };
}

export interface AdminRetryableRotationIdentity {
	id: string;
}

const ADMIN_SMALL_FILE_ORIGIN = "contact-admin-small-file";
const ADMIN_SMALL_FILE_LEASE_TTL_MS = 2 * 60 * 1000;
const SMALL_FILE_TARGET_TIMEOUT_MS = 30_000;
const ADMIN_ADMISSION_RETRY_DELAY_MS = 5_000;
const SMALL_FILE_TRANSPORTS = new Set(["direct-put", "multipart"] as const);
const AUTHORITATIVE_MULTIPART_FACADE_ERROR_CODES = new Set([
	"BYTES_GONE",
	"OPERATION_NOT_SUPPORTED",
	"ORGANIZATION_DELETED",
	"REPLACEMENT_IN_PROGRESS",
	"UPLOAD_ABORT_UNSAFE",
	"UPLOAD_ACCESS_DENIED",
	"UPLOAD_ATTEMPT_FENCE_INVALID",
	"UPLOAD_CANCELLED",
	"UPLOAD_IDENTITY_UNVERIFIABLE",
	"UPLOAD_INTENT_CONFLICT",
	"UPLOAD_LOST",
	"UPLOAD_MANIFEST_CONFLICT",
	"UPLOAD_MANIFEST_INCOMPLETE",
	"UPLOAD_NOT_FOUND",
	"UPLOAD_TERMINAL",
	"UPLOAD_OPERATION_IN_PROGRESS",
	"UPLOAD_RECONCILIATION_REQUIRED",
	"UPLOAD_REJECTED",
	"UPLOAD_ROTATION_SUPERSEDED",
	"UPLOAD_SUPERSEDED",
	"UPLOAD_TRANSPORT_UNSUPPORTED"
]);
type AdminSmallFileTransport = "direct-put" | "multipart";

function abortCleanupStillPendingError(): Error & { code: string; retryable: true } {
	return Object.assign(new Error("Admin upload abort is still settling [UPLOAD_OPERATION_IN_PROGRESS]"), {
		code: "UPLOAD_OPERATION_IN_PROGRESS",
		retryable: true as const
	});
}
/**
 * How long an interactive caller may stay parked on a *retryable* admission miss before it is told
 * the upload failed. A retryable miss (the item writer is held by another tab, or the durable
 * bridge is momentarily unavailable) leaves the durable row pending and schedules another drain, so
 * settling the waiter on the miss itself reports failure for an upload that then succeeds. Three
 * admission windows is the ceiling: a waiter that never learns an outcome is its own bug, and the
 * durable row keeps retrying in the background after the caller has been released.
 */
const ADMIN_ADMISSION_WAITER_BUDGET_MS = 3 * ADMIN_ADMISSION_RETRY_DELAY_MS;
/**
 * LSP-19929: the server's direct-put ceiling (`DIRECT_PUT_MAX_BYTES`, default 10 MiB). Payloads at or
 * above it go straight to the multipart lane instead of probing direct-put first — the probe was
 * always answered `400 UPLOAD_TRANSPORT_UNSUPPORTED`, which showed up as an error in every large
 * rotation's console and in Datadog although the fallback then succeeded. The probe-then-fall-back
 * path stays as the authority for any environment whose ceiling is configured lower than this.
 */
const DIRECT_PUT_MAX_BYTES_HINT = 10 * 1024 * 1024;
/**
 * LSP-19929: how long an interactive caller may keep waiting on a completion receipt the server has
 * acknowledged but not yet finalized. The durable row keeps re-reading its receipt in the background
 * on the shared lane's settling cadence (never re-uploading); this budget only decides when the
 * rotation dialog stops spinning. A finalizer normally lands in 10-15 s.
 */
const ADMIN_FINALIZING_WAITER_BUDGET_MS = 3 * 60_000;
const COMPLETION_RECEIPT_ABSENT_STRIKES = 3;

/**
 * The DURABLE ceiling on the server's settling verdict now lives in the shared uploads package
 * (`settlingCeiling.ts` — `SETTLING_CONFLICT_CEILING_MS`, enforced by `SmallFileUploadLane` around
 * every `presign`), because the retryable `UPLOAD_OPERATION_IN_PROGRESS` verdict it bounds is
 * produced by `ensureUploadSession` for EVERY caller of the registry helper, not just this lane.
 *
 * What stays here is the durable half the shared code cannot own: `AdminSmallFileUpload.
 * settlingConflictSince` is this app's own IndexedDB column, and it is what makes the window
 * survive the reload that makes the retry loop unbounded in the first place. The
 * `readSettlingWindow`/`armSettlingWindow` store hooks below are that column, nothing more.
 */

function persistFailureReason(error: unknown): string {
	if (error instanceof Error) return error.message;
	if (typeof error === "string") return error;
	try {
		return JSON.stringify(error) ?? String(error);
	} catch {
		return String(error);
	}
}

function isAdminSmallFileTransport(value: unknown): value is AdminSmallFileTransport {
	return typeof value === "string" && SMALL_FILE_TRANSPORTS.has(value as AdminSmallFileTransport);
}

function containsControlCharacter(value: string): boolean {
	for (const character of value) {
		const characterCode = character.charCodeAt(0);
		if (characterCode <= 0x1f || characterCode === 0x7f) return true;
	}
	return false;
}

function normalizeActiveMultipartCancellation(error: unknown): unknown {
	if (!error || typeof error !== "object") return error;
	const details = error as {
		cancelled?: unknown;
		code?: unknown;
		errorCode?: unknown;
		message?: unknown;
		name?: unknown;
		status?: unknown;
	};
	const cancellationMarker = (value: unknown) =>
		typeof value === "string" && (value.toUpperCase().includes("ABORTERROR") || value.toUpperCase().includes("CANCEL"));
	const facadeErrorCode = readUploadFacadeErrorCode(error);
	// Typed facade verdicts outrank cancellation-shaped prose and flags. `errorCode` is the admin
	// transport's authoritative server field; the shared facade client uses string `code` plus the
	// numeric response status. Keep the two local control-flow codes authoritative as well.
	if (
		facadeErrorCode !== undefined &&
		(facadeErrorCode === details.errorCode ||
			(AUTHORITATIVE_MULTIPART_FACADE_ERROR_CODES.has(facadeErrorCode) &&
				Number.isInteger(details.status) &&
				(details.status as number) >= 400 &&
				(details.status as number) <= 599) ||
			facadeErrorCode === "UPLOAD_CANCELLED" ||
			facadeErrorCode === "UPLOAD_ROTATION_SUPERSEDED")
	) {
		return error;
	}
	if (
		details.cancelled !== true &&
		!cancellationMarker(details.name) &&
		!cancellationMarker(details.code) &&
		!cancellationMarker(details.message)
	) {
		return error;
	}
	try {
		// Multipart facade calls do not receive the lane's teardown signal. An AbortError while the
		// lane is active is therefore a transport interruption, and the shared durable retry policy
		// must not mistake browser/library cancellation-shaped fields for an authoritative user action.
		Object.defineProperties(error, {
			name: { configurable: true, value: "NetworkError" },
			cancelled: { configurable: true, value: false },
			...(cancellationMarker(details.code) ? { code: { configurable: true, value: "NetworkError" } } : {}),
			...(cancellationMarker(details.message) ? { message: { configurable: true, value: "Multipart request was interrupted" } } : {})
		});
		return error;
	} catch {
		const normalizedError = new TypeError("Multipart request was interrupted");
		Object.defineProperty(normalizedError, "cause", { configurable: true, value: error });
		return normalizedError;
	}
}

function exactAttemptIdentity(
	value: unknown,
	expectedTransport?: AdminSmallFileTransport
): SmallFileCompletionReceiptCheckpoint | undefined {
	if (!value || typeof value !== "object") return undefined;
	const projection = value as {
		id?: unknown;
		attemptId?: unknown;
		generation?: unknown;
		attempt?: { id?: unknown; generation?: unknown; transport?: unknown; takeGeneration?: unknown } | null;
	};
	if (
		typeof projection.id !== "string" ||
		projection.id.trim() !== projection.id ||
		projection.id.length === 0 ||
		containsControlCharacter(projection.id) ||
		typeof projection.attemptId !== "string" ||
		projection.attemptId.trim() !== projection.attemptId ||
		projection.attemptId.length === 0 ||
		containsControlCharacter(projection.attemptId) ||
		!Number.isSafeInteger(projection.generation) ||
		(projection.generation as number) < 1 ||
		projection.attempt?.id !== projection.attemptId ||
		projection.attempt.generation !== projection.generation ||
		!isAdminSmallFileTransport(projection.attempt.transport) ||
		(expectedTransport !== undefined && projection.attempt.transport !== expectedTransport)
	) {
		return undefined;
	}
	return {
		registryId: projection.id,
		attemptId: projection.attemptId,
		generation: projection.generation as number,
		takeGeneration:
			typeof projection.attempt.takeGeneration === "string" && projection.attempt.takeGeneration.length > 0
				? projection.attempt.takeGeneration
				: undefined
	};
}

function exactReadyReceipt(value: unknown, expectedTransport?: AdminSmallFileTransport): SmallFileCompletionReceiptCheckpoint | undefined {
	if (!value || typeof value !== "object") return undefined;
	const projection = value as { kind?: unknown; transport?: unknown; status?: unknown; targets?: unknown };
	if (
		projection.kind !== "image" ||
		!isAdminSmallFileTransport(projection.transport) ||
		(expectedTransport !== undefined && projection.transport !== expectedTransport) ||
		projection.status !== "ready" ||
		!Array.isArray(projection.targets) ||
		projection.targets.length !== 0
	) {
		return undefined;
	}
	return exactAttemptIdentity(value, projection.transport);
}

function isExactCurrentReadyReceipt(value: unknown, expected: SmallFileCompletionReceiptCheckpoint): boolean {
	if (!value || typeof value !== "object") return false;
	const projection = value as {
		id?: unknown;
		kind?: unknown;
		transport?: unknown;
		status?: unknown;
		attempt?: { id?: unknown; generation?: unknown; transport?: unknown } | null;
	};
	return (
		projection.id === expected.registryId &&
		projection.kind === "image" &&
		isAdminSmallFileTransport(projection.transport) &&
		projection.status === "ready" &&
		projection.attempt?.id === expected.attemptId &&
		projection.attempt.generation === expected.generation &&
		projection.attempt.transport === projection.transport
	);
}

function classifyAdminSmallFileReceipt(projection: unknown, expected: SmallFileCompletionReceiptCheckpoint): SmallFileReceiptState {
	if (!projection || typeof projection !== "object") return "unknown";
	const receipt = projection as {
		id?: unknown;
		kind?: unknown;
		transport?: unknown;
		status?: unknown;
		attempt?: { id?: unknown; generation?: unknown; transport?: unknown } | null;
	};
	const identityMatches =
		receipt.id === expected.registryId &&
		receipt.kind === "image" &&
		isAdminSmallFileTransport(receipt.transport) &&
		receipt.attempt?.id === expected.attemptId &&
		receipt.attempt.generation === expected.generation &&
		receipt.attempt.transport === receipt.transport;
	if (!identityMatches) return "unknown";
	if (receipt.status === "ready") return "received";
	return typeof receipt.status === "string" && FINALIZING_RECEIPT_STATUSES.has(receipt.status) ? "pending" : "unknown";
}

/** LSP-19929: identity matches THIS attempt and the status is a string that is neither finalizing nor ready. */
function isExactAttemptTerminalReceipt(projection: unknown, expected: SmallFileCompletionReceiptCheckpoint): boolean {
	if (!projection || typeof projection !== "object") return false;
	const receipt = projection as {
		id?: unknown;
		kind?: unknown;
		transport?: unknown;
		status?: unknown;
		attempt?: { id?: unknown; generation?: unknown; transport?: unknown } | null;
	};
	return (
		receipt.id === expected.registryId &&
		receipt.kind === "image" &&
		isAdminSmallFileTransport(receipt.transport) &&
		receipt.attempt?.id === expected.attemptId &&
		receipt.attempt.generation === expected.generation &&
		receipt.attempt.transport === receipt.transport &&
		typeof receipt.status === "string" &&
		receipt.status !== "ready" &&
		!FINALIZING_RECEIPT_STATUSES.has(receipt.status)
	);
}

/**
 * LSP-19929 (AI review on PR 5250): the server still holds THIS registry id but its well-formed current
 * attempt identity differs from the checkpointed one. That attempt has been superseded on the server and
 * can never again read as ready, so for a completion receipt it is as definitive as a 404. A projection
 * whose attempt identity is missing or malformed is NOT superseded — it stays "unknown".
 */
function isSupersededAttemptReceipt(projection: unknown, expected: SmallFileCompletionReceiptCheckpoint): boolean {
	if (!projection || typeof projection !== "object") return false;
	const receipt = projection as {
		id?: unknown;
		kind?: unknown;
		transport?: unknown;
		attempt?: { id?: unknown; generation?: unknown; transport?: unknown } | null;
	};
	return (
		receipt.id === expected.registryId &&
		receipt.kind === "image" &&
		isAdminSmallFileTransport(receipt.transport) &&
		typeof receipt.attempt?.id === "string" &&
		receipt.attempt.id.length > 0 &&
		typeof receipt.attempt.generation === "number" &&
		Number.isFinite(receipt.attempt.generation) &&
		receipt.attempt.transport === receipt.transport &&
		(receipt.attempt.id !== expected.attemptId || receipt.attempt.generation !== expected.generation)
	);
}

function exactActiveTarget(value: unknown): SmallFilePresignTarget | undefined {
	if (!value || typeof value !== "object") return undefined;
	const projection = value as {
		kind?: unknown;
		transport?: unknown;
		status?: unknown;
		targets?: unknown;
	};
	const identity = exactAttemptIdentity(value, "direct-put");
	if (
		!identity ||
		projection.kind !== "image" ||
		projection.transport !== "direct-put" ||
		(projection.status !== "requested" && projection.status !== "uploading") ||
		!Array.isArray(projection.targets) ||
		projection.targets.length !== 1
	) {
		return undefined;
	}
	const target = projection.targets[0] as Record<string, unknown> | undefined;
	if (!target || target.attemptId !== identity.attemptId || target.generation !== identity.generation || target.ordinal !== 1) {
		return undefined;
	}
	return {
		registryId: identity.registryId,
		attemptId: identity.attemptId,
		generation: identity.generation,
		takeGeneration: identity.takeGeneration,
		targetId: target.targetId as string,
		url: target.url as string,
		headers: target.headers as Readonly<Record<string, string>>,
		expiresAt: target.expiresAt as string,
		expectedBytes: target.expectedBytes as number | null,
		expectedDigest: target.expectedDigest as string | null
	};
}

function exactMultipartActiveTargets(
	value: unknown,
	expectedPartCount: number
): { identity: SmallFileCompletionReceiptCheckpoint; targets: UploadTargetEnvelope[] } | undefined {
	if (!value || typeof value !== "object") return undefined;
	const projection = value as { kind?: unknown; transport?: unknown; status?: unknown; targets?: unknown };
	const identity = exactAttemptIdentity(value, "multipart");
	if (
		!identity ||
		projection.kind !== "image" ||
		projection.transport !== "multipart" ||
		(projection.status !== "requested" && projection.status !== "uploading") ||
		!Array.isArray(projection.targets) ||
		projection.targets.length !== expectedPartCount
	) {
		return undefined;
	}
	try {
		return {
			identity,
			targets: projection.targets.map((target, index) => {
				const validatedTarget = validateUploadTargetEnvelope(
					target as UploadTargetEnvelope,
					index + 1,
					identity.attemptId,
					identity.generation
				);
				if (containsControlCharacter(validatedTarget.targetId) || containsControlCharacter(validatedTarget.url)) {
					throw new Error("Admin multipart target contains a control character");
				}
				return validatedTarget;
			})
		};
	} catch {
		return undefined;
	}
}

function recoverAdminPayload(record: AdminSmallFileUpload): Blob | undefined {
	if (record.payload && record.payload.size > 0 && typeof record.payload.arrayBuffer === "function") return record.payload;
	if (record.payloadBytes?.byteLength) return new Blob([record.payloadBytes], { type: record.fileType });
	return undefined;
}

function asAdminRecord(record: AdminSmallFileUpload, payload: Blob): SmallFileRecord {
	return {
		contractVersion: record.contractVersion,
		protocolSelectionPending: record.protocolSelectionPending,
		objectUploadJson: record.objectUploadJson,
		objectAttempt: record.objectAttempt,
		savedSnapshotId: record.savedSnapshotId,
		id: record.id,
		conversationId: record.conversationId,
		organizationId: record.organizationId,
		payload,
		fileName: record.fileName,
		fileType: record.fileType,
		category: record.category,
		idempotencyId: record.idempotencyId,
		metadata: {
			...(record.metadata ?? {}),
			adminEndpointFamily: record.endpointFamily,
			adminOperation: record.operation,
			adminOrganizationId: record.organizationId,
			adminSnapshotId: record.snapshotId,
			adminParentId: record.parentId
		},
		createdAt: record.createdAt,
		retryAt: record.retryAt,
		retryAttempt: record.retryAttempt,
		error: record.lastError,
		status: record.status,
		target: record.target,
		completionReceipt: record.completionReceipt,
		// LSP-19912: projected so the shared lane can tell, without a second durable read on every
		// healthy presign, whether this row has a settling window to close. See
		// `SettlingCeilingOptions.knownOpenedAt`.
		settlingConflictSince: record.settlingConflictSince
	};
}

function asRecord(record: SmallFileRecord, existing?: AdminSmallFileUpload): AdminSmallFileUpload {
	const metadata = record.metadata ?? {};
	return {
		contractVersion: record.contractVersion,
		protocolSelectionPending: record.protocolSelectionPending,
		objectUploadJson: record.objectUploadJson,
		objectAttempt: record.objectAttempt,
		savedSnapshotId: record.savedSnapshotId,
		id: record.id,
		conversationId: record.conversationId,
		organizationId: record.organizationId ?? metadata.adminOrganizationId as string | undefined,
		payload: record.payload,
		fileName: record.fileName,
		fileType: record.fileType,
		category: record.category,
		operation: (metadata.adminOperation as AdminSmallFileOperation | undefined) ?? existing?.operation ?? "snapshot",
		snapshotId: (metadata.adminSnapshotId as string | undefined) ?? existing?.snapshotId,
		parentId: (metadata.adminParentId as string | null | undefined) ?? existing?.parentId,
		idempotencyId: record.idempotencyId ?? existing?.idempotencyId,
		metadata: existing?.metadata ?? metadata,
		endpointFamily: existing?.endpointFamily ?? "uploads",
		target: record.target ?? existing?.target,
		completionReceipt: record.completionReceipt ?? existing?.completionReceipt,
		createdAt: record.createdAt,
		retryAt: record.retryAt,
		retryAttempt: record.retryAttempt,
		status: record.status === "auth-gated" ? "pending" : record.status,
		lastError: existing?.lastError
	};
}

function adminSmallFileDetails(record: AdminSmallFileUpload) {
	const metadata = record.metadata ?? {};
	return {
		originalFileName: record.fileName,
		fileExtension: record.fileName.split(".").pop() ?? "bin",
		fileType: record.fileType,
		...(record.idempotencyId ? { idempotencyId: record.idempotencyId } : {}),
		...(typeof metadata.sourceMasterRecordingId === "string" ? { sourceMasterRecordingId: metadata.sourceMasterRecordingId } : {}),
		...(typeof metadata.sourceMasterRecordingOffsetMs === "number" && Number.isSafeInteger(metadata.sourceMasterRecordingOffsetMs)
			? { sourceMasterRecordingOffsetMs: metadata.sourceMasterRecordingOffsetMs }
			: {}),
		...(record.operation === "rotation" && record.snapshotId ? { snapshotId: record.snapshotId } : {})
	};
}

function withTargetTimeout<T>(
	operation: Promise<T>,
	onTimeout?: (error: Error) => void,
	timeoutMessage = `Snapshot upload target request timed out after ${SMALL_FILE_TARGET_TIMEOUT_MS}ms`
): Promise<T> {
	const operationPromise = Promise.resolve(operation);
	let timer: ReturnType<typeof setTimeout> | undefined;
	return new Promise<T>((resolve, reject) => {
		timer = setTimeout(() => {
			const error = new Error(timeoutMessage);
			onTimeout?.(error);
			reject(error);
		}, SMALL_FILE_TARGET_TIMEOUT_MS);
		operationPromise.then(resolve, reject).finally(() => timer !== undefined && clearTimeout(timer));
	});
}

/**
 * Admin's single durable byte lane. Snapshot extraction, annotation replacement, and in-place
 * rotation all persist the bytes into this adapter before any presign/PUT. This store is introduced
 * by the unshipped v5 schema, so every durable row is unconditionally uploads-family.
 */
export class AdminSmallFileUploadLane {
	private readonly lane: SmallFileUploadLane;
	private readonly completionResults = new Map<string, AdminSmallFileUploadResult>();
	// ponytail: rejected fresh writes survive only this page; reload durability requires working IndexedDB.
	// Keep failed bytes for user retry; uploaded rows retain only identity metadata to avoid duplicate uploads.
	private readonly memoryRows = new Map<string, AdminSmallFileUpload>();
	private readonly ownerId = `admin-small-file:${newUploadId()}`;
	private readonly writerTokens = new Map<string, DurableLeaseToken>();
	private readonly waiters = new Map<
		string,
		Set<{ resolve: (result: AdminSmallFileUploadResult) => void; reject: (error: unknown) => void }>
	>();
	/** Detached aborts keyed by the stable server-side rotation target they are releasing. */
	private readonly abortCleanupsByRotationTarget = new Map<string, Set<Promise<void>>>();
	private admissionRetryTimer: ReturnType<typeof setTimeout> | undefined;
	private r0RequeueRetryTimer: ReturnType<typeof setTimeout> | undefined;
	/** Per-item deadline that bounds how long a waiter is parked on retryable admission misses. */
	private readonly admissionBudgets = new Map<string, { timer: ReturnType<typeof setTimeout>; error: unknown }>();
	/** LSP-19929: per-row timer that releases a waiter still held on a checkpointed, not-yet-ready completion receipt. */
	private readonly finalizingBudgets = new Map<string, ReturnType<typeof setTimeout>>();
	/** The R-0 startup requeue is a one-shot migration, not part of the recurring drain. */
	private r0RequeueDone = false;
	private destroyed = false;

	constructor(private readonly api: AdminSmallFileApi) {
		const store: SmallFileStore = {
			checkpointObjectUpload: async (id, choice, expected) => {
				const current = await this.getRecord(id);
				if (
					!current ||
					current.endpointFamily !== "uploads" ||
					current.target ||
					current.completionReceipt ||
					current.status === "needs-attention" ||
					current.createdAt !== expected.createdAt ||
					current.idempotencyId !== expected.idempotencyId ||
					current.organizationId !== expected.organizationId ||
					current.conversationId !== expected.conversationId
				)
					return undefined;
				const selected = selectSmallFileObjectControl(current, choice);
				await this.updateRecord(id, selected);
				if (selected.objectAttempt && !this.finalizingBudgets.has(id)) this.armFinalizingWaiterBudget(id);
				return selected;
			},

			get: async (id) => {
				const retained = this.memoryRows.get(id);
				if (retained) {
					const payload = recoverAdminPayload(retained);
					return payload ? asAdminRecord(retained, payload) : undefined;
				}
				return db.transaction("rw", db.smallFileUploads, async () => {
					const record = await this.getRecord(id);
					if (!record) return undefined;
					const payload = recoverAdminPayload(record);
					if (payload) return asAdminRecord(record, payload);
					if (record.status === "pending") {
						await this.updateRecord(id, {
							status: "needs-attention",
							retryAt: null,
							lastError: "Local upload payload is missing or unreadable."
						});
					}
					return undefined;
				});
			},
			put: async (record) => {
				const candidate = asRecord(record);
				const payloadBytes = await record.payload.arrayBuffer();
				const committed = await this.commit(candidate.id, async () => {
					const current = await this.getRecord(candidate.id);
					if (current?.status === "uploaded") return;
					if (current?.contractVersion === 2) {
						if (current.idempotencyId !== candidate.idempotencyId) throw new Error("Upload intent changed before persistence");
						return { reusedCompletionReceipt: current.objectAttempt !== undefined };
					}
					const replacesRetryableRotation =
						current?.operation === "rotation" &&
						candidate.operation === "rotation" &&
						current.snapshotId === candidate.snapshotId;
					if (
						current &&
						(current.conversationId !== candidate.conversationId ||
							(current.idempotencyId !== candidate.idempotencyId && !replacesRetryableRotation))
					) {
						throw new Error("Admin small-file idempotency identity changed");
					}
					// LSP-19929 (Bugbot on PR 5250): a user retry of a rotation whose bytes the server has
					// ALREADY acknowledged (the durable row holds a completion receipt and is still pending,
					// i.e. finalizing) must not become a second attempt. Spreading the fresh candidate over the
					// row would erase the receipt and mint a new idempotency identity, so the engine would
					// re-presign against an owner that is still finalizing and the retry could park on
					// REPLACEMENT_IN_PROGRESS for a rotation that in fact succeeded. Keep the receipt and the
					// identity it belongs to: the engine re-reads it and the waiter resolves when it is ready.
					// A needs-attention row (receipt proven lost) deliberately does not qualify, so its retry
					// still re-mints.
					const retriesFinalizingCompletion =
						current?.status === "pending" && current.completionReceipt !== undefined && replacesRetryableRotation;
					if (retriesFinalizingCompletion) {
						console.info("[Upload] Admin small-file retry reuses the acknowledged completion receipt instead of re-minting", {
							id: candidate.id,
							registryId: current.completionReceipt!.registryId,
							attemptId: current.completionReceipt!.attemptId,
							generation: current.completionReceipt!.generation
						});
					}
					await this.putRecord(
						current
							? {
									...current,
									...candidate,
									payload: undefined,
									payloadBytes,
									status: "pending",
									retryAt: null,
									retryAttempt: 0,
									// A re-enqueue is a fresh request, so it also restarts the settling ceiling.
									settlingConflictSince: undefined,
									lastError: undefined,
									...(retriesFinalizingCompletion
										? { completionReceipt: current.completionReceipt, idempotencyId: current.idempotencyId }
										: {})
								}
							: { ...candidate, payload: undefined, payloadBytes, status: "pending", retryAt: null, retryAttempt: 0 }
					);
					return { reusedCompletionReceipt: retriesFinalizingCompletion };
				});
				if (committed === false) throw new Error("Admin small-file durable write lost writer ownership");
				// The retry's waiter needs its own release deadline: the engine will not checkpoint the
				// receipt again (it is already durable), so the checkpoint hook will not arm one.
				if (committed?.reusedCompletionReceipt) this.armFinalizingWaiterBudget(candidate.id);
			},
			listPending: async () => {
				// Recovery wakes reach this scan before the ownership adapter can reopen a dropped
				// connection. Keep the retry outside the transaction so its writes stay atomic.
				return withIdbReopen(
					() =>
						db.transaction("rw", db.smallFileUploads, async () => {
							const pending: SmallFileRecord[] = [...this.memoryRows.values()]
								.filter((record) => record.status === "pending")
								.map((record) => asAdminRecord(record, recoverAdminPayload(record)!));
							for (const record of await db.smallFileUploads.where("status").equals("pending").toArray()) {
								const payload = recoverAdminPayload(record);
								if (payload) pending.push(asAdminRecord(record, payload));
								else {
									await this.updateRecord(record.id, {
										status: "needs-attention",
										retryAt: null,
										lastError: "Local upload payload is missing or unreadable."
									});
								}
							}
							return pending;
						}),
					{ payloadScan: true }
				);
			},
			markUploaded: async (id, expected) => {
				const current = await this.getRecord(id);
				if (!current) return false;
				if (expected && (current.createdAt !== expected.createdAt || current.idempotencyId !== expected.idempotencyId))
					return false;
				if (current.status !== "uploaded")
					await this.updateRecord(id, {
						status: "uploaded",
						retryAt: null,
						...(current.contractVersion === 2 ? { payload: undefined, payloadBytes: undefined } : {})
					});
				if (current.contractVersion === 2 && Dexie.currentTransaction)
					Dexie.currentTransaction.on("complete", () => this.resolve(id));
				else this.resolve(id);
				return true;
			},
			updateRetry: async (id, update, expected) => {
				const current = await this.getRecord(id);
				if (!current) return false;
				if (expected && (current.createdAt !== expected.createdAt || current.idempotencyId !== expected.idempotencyId))
					return false;
				const outcome = await this.updateRecord(id, {
					retryAt: update.retryAt,
					retryAttempt: update.retryAttempt,
					status: update.status === "auth-gated" ? "pending" : (update.status ?? "pending"),
					lastError: update.error === undefined ? current.lastError : persistFailureReason(update.error)
				});
				if (
					update.error !== undefined &&
					!isReceiptConfirmationPendingError(update.error) &&
					!(current.contractVersion === 2 && readUploadFacadeErrorCode(update.error) === "UPLOAD_VERIFICATION_PENDING") &&
					(update.status === "needs-attention" || classifyUploadFailure(update.error).kind !== "refresh")
				) {
					this.reject(id, update.error);
				}
				if (
					outcome > 0 &&
					current.status !== "needs-attention" &&
					update.status === "needs-attention" &&
					isUploadTerminalFailure(update.error)
				) {
					const checkpoint = current.target ?? current.completionReceipt;
					adminUploadTelemetry.logInfo("recording.fileUpload.terminalParked", {
						uploadId: checkpoint?.registryId,
						registryId: checkpoint?.registryId,
						trigger: readUploadRefusalTrigger(update.error) ?? "urls",
						...readUploadTerminalDetails(update.error),
						rowAttemptId: checkpoint?.attemptId,
						rowGeneration: checkpoint?.generation,
						retryAttempt: update.retryAttempt
					});
				}
				return outcome > 0;
			},
			checkpointTarget: async (id, target, expected) => {
				const current = await this.getRecord(id);
				if (!current || current.endpointFamily !== "uploads") return false;
				if (expected && (current.createdAt !== expected.createdAt || current.idempotencyId !== expected.idempotencyId)) {
					return false;
				}
				const outcome = await this.updateRecord(id, {
					target: { ...target, headers: { ...target.headers } },
					contractVersion: 1,
					protocolSelectionPending: false
				});
				return outcome > 0;
			},
			readSettlingWindow: async (id) => (await this.getRecord(id))?.settlingConflictSince,
			armSettlingWindow: async (id, openedAt) => {
				await this.updateRecord(id, { settlingConflictSince: openedAt });
			},
			// Dexie deletes a key whose update value is `undefined`, so closing the window is the same
			// one-column write that armed it, with no instant. `put` above discards it on re-enqueue
			// for the same "this request is fresh" reason; this closes it on a presign that SUCCEEDS,
			// which is the only other positive evidence that the settling episode ended.
			clearSettlingWindow: async (id) => {
				await this.updateRecord(id, { settlingConflictSince: undefined });
			},
			checkpointCompletionReceipt: async (id, receipt, expected) => {
				const current = await this.getRecord(id);
				if (!current || current.endpointFamily !== "uploads") return false;
				if (expected && (current.createdAt !== expected.createdAt || current.idempotencyId !== expected.idempotencyId)) {
					return false;
				}
				const outcome = await this.updateRecord(id, {
					completionReceipt: { ...receipt },
					contractVersion: 1,
					protocolSelectionPending: false
				});
				if (outcome > 0) this.armFinalizingWaiterBudget(id);
				return outcome > 0;
			},
			recoverAnomalous: async (expected, replacement, audit) => {
				const current = await this.getRecord(expected.id);
				if (!current || current.createdAt !== expected.createdAt || current.idempotencyId !== expected.idempotencyId) return false;
				const replacementRecord = asRecord(replacement);
				const payloadBytes = await replacement.payload.arrayBuffer();
				await this.updateRecord(expected.id, {
					status: "needs-attention",
					lastError: JSON.stringify({ code: "UPLOAD_ANOMALY", audit }),
					metadata: {
						...(current.metadata ?? {}),
						anomalyRecoveryOf: expected.id,
						anomalyRecovery: audit
					}
				});
				await this.putRecord({
					...replacementRecord,
					payload: undefined,
					payloadBytes,
					status: "pending",
					retryAt: null,
					retryAttempt: 0
				});
				return true;
			}
		};

		this.lane = new SmallFileUploadLane({
			objectUploads: {
				telemetry: adminUploadTelemetry,
				client: () => this.api._uploadsFacade(),
				waitForProcessing: true,
				createRequest: (record): UploadsAnyCreateRequest => {
					const domain = asRecord(record);
					const common = {
						type: constants.DOCUMENT_TYPE.IMAGE,
						supportedTransports: ["chunked-object"] as const,
						idempotencyId: record.idempotencyId ?? record.id,
						fileDetails: [adminSmallFileDetails(domain)]
					};
					if (domain.operation === "logo" || domain.operation === "virtual-background") {
						if (!domain.organizationId || domain.conversationId)
							throw uploadControlError("Organization upload scope is invalid");
						return {
							...common,
							supportedTransports: ["chunked-object"],
							kind: domain.operation,
							organizationId: domain.organizationId,
							createdVia: `organization-${domain.operation}`
						};
					}
					if (!record.conversationId || record.organizationId) throw uploadControlError("Snapshot upload scope is invalid");
					return { ...common, supportedTransports: ["chunked-object"], kind: "image", conversationId: record.conversationId };
				}
			},

			store,
			wire: { presign: (input) => this.presign(input) },
			transport: createBlobPutTransport(testFetch),
			requireServerReceipt: true,
			receiptOracle: ({ record, checkpoint, kind }) =>
				record.contractVersion === 2 ? this.readObjectProcessingReceipt(record, checkpoint) : this.readReceipt(checkpoint, kind),
			targetObjectExists: ({ checkpoint }) => this.readObjectExists(checkpoint),
			requiresCompletionReceipt: (record) =>
				(record.metadata?.adminEndpointFamily === "uploads" ||
					record.target !== undefined ||
					record.completionReceipt !== undefined) &&
				record.status === "uploaded",
			claim: (record) => this.claim(record),
			onDrainError: (error) => this.rejectAll(error),
			// R-0 (owner directive 2026-08-26): a backoff-tier boundary, not a budget. Annotation and
			// rotation saves keep retrying forever on the slow curve rather than parking after three
			// failures; offline failures cost no attempt.
			slowRetryAfterAttempts: 3,
			poison: {
				originKey: ADMIN_SMALL_FILE_ORIGIN,
				identity: (record) => ({ itemId: record.id }),
				getOwnership: () => getAdminDurableOwnershipForCoordinator(),
				ceiling: 3
			},
			retryRefreshImmediately: true,
			// LSP-19929: a multipart rotation hands its completion receipt to the engine while the server is
			// still finalizing; a registry the server then repeatedly no longer holds must park, not loop.
			completionReceiptFinalizing: { absentStrikes: COMPLETION_RECEIPT_ABSENT_STRIKES },
			retryDelayMs: (attempt, now) => now + 250 * attempt * attempt
		});
	}

	/** Returns undefined only for an explicit old-server cohort, before any v2 source is admitted. */
	async enqueueOrganization(request: {
		id?: string;
		organizationId: string;
		kind: "logo" | "virtual-background";
		blob: Blob;
	}): Promise<{ organization: NonNullable<AdminSmallFileUploadResult["organization"]> } | undefined> {
		if (!request.organizationId || request.blob.size <= 0) throw uploadControlError("Organization image source is invalid");
		const current = request.id ? await this.getRecord(request.id) : undefined;
		if (current && (current.organizationId !== request.organizationId || current.conversationId || current.operation !== request.kind))
			throw uploadControlError("Upload source scope changed");
		// A retained source already chose its protocol; discovery cannot downgrade or prevent its replay.
		const objectControl = current ? readUploadKnownSizeControl(current) : await planKnownSizeUploadSource({
			telemetry: adminUploadTelemetry,
			kind: request.kind,
			capabilities: await this.api._uploadsFacade()
				.capabilities({ kind: request.kind, organizationId: request.organizationId, sourceMode: "known-size" }),
			byteSize: request.blob.size,
			contentType: request.blob.type || "application/octet-stream",
			chunkSize: CHUNK_SIZE,
			readPart: async (offset, size) => request.blob.slice(offset, offset + size)
		});
		if (current && !objectControl) throw uploadControlError("Saved organization upload source is unreadable");
		if (!objectControl) return undefined;
		const id = request.id ?? newUploadId();
		// Planning is pure. enqueueAndWait commits the original bytes and immutable choice before create/PUT.
		const result = await this.enqueueAndWait({
			id,
			organizationId: request.organizationId,
			operation: request.kind,
			blob: request.blob,
			idempotencyId: current?.idempotencyId ?? id,
			objectControl
		});
		// Completed generic rows have no in-memory result after restart. Read the product, never re-upload it.
		const organization = result.organization ?? await this.api.get?.(`/organizations/${encodeURIComponent(request.organizationId)}`);
		if (!organization || organization.id !== request.organizationId)
			throw uploadControlError("Completed organization upload result is unreadable");
		return { organization };
	}

	async enqueueAndWait(request: AdminSmallFileUploadRequest): Promise<AdminSmallFileUploadResult> {
		const organizationImage = request.operation === "logo" || request.operation === "virtual-background";
		if (
			organizationImage
				? !request.organizationId || !!request.conversationId || !request.objectControl
				: !request.conversationId || !!request.organizationId
		)
			throw uploadControlError("Small-file source scope is invalid");
		if (request.blob.size <= 0) throw new Error("Cannot upload an empty snapshot payload");
		const current = await this.getRecord(request.id);
		if (current && (current.organizationId !== request.organizationId || current.conversationId !== request.conversationId || current.operation !== request.operation))
			throw uploadControlError("Upload source scope changed");
		if (current?.status === "uploaded") return {};
		if (current?.contractVersion === 2 && current.idempotencyId !== request.idempotencyId) {
			// A new edit cannot replace an admitted source whose acceptance may still be in flight.
			throw Object.assign(new Error("Another edit is still being saved."), {
				code: "REPLACEMENT_IN_PROGRESS",
				errorCode: "REPLACEMENT_IN_PROGRESS",
				error: "REPLACEMENT_IN_PROGRESS",
				httpStatus: 409
			});
		}

		if (
			current?.operation === "rotation" &&
			request.operation === "rotation" &&
			current.snapshotId === request.snapshotId &&
			current.idempotencyId !== request.idempotencyId &&
			current.target
		) {
			try {
				await (current.target.takeGeneration
					? this.api.abortUploadsUpload(
							current.target.registryId,
							current.target.attemptId,
							current.target.generation,
							"direct-put",
							current.target.takeGeneration
						)
					: this.api.abortUploadsUpload(
							current.target.registryId,
							current.target.attemptId,
							current.target.generation,
							"direct-put"
						));
			} catch (error) {
				console.warn("Failed to abort replaced Admin rotation target", {
					registryId: current.target.registryId,
					...uploadFailureLogDetails(error)
				});
			}
		}
		const result = new Promise<AdminSmallFileUploadResult>((resolve, reject) => {
			const pending = this.waiters.get(request.id) ?? new Set();
			pending.add({ resolve, reject });
			this.waiters.set(request.id, pending);
			request.signal?.addEventListener("abort", () => this.reject(request.id, new Error("Admin small-file upload was aborted")), {
				once: true
			});
		});
		try {
			await this.lane.enqueue({
				id: request.id,
				conversationId: request.conversationId,
				organizationId: request.organizationId,
				payload: request.blob,
				...(request.objectControl ? { contractVersion: 2 as const, objectUploadJson: JSON.stringify(request.objectControl) } : {}),
				protocolSelectionPending:
					!request.objectControl && !current?.target && !current?.completionReceipt && current?.contractVersion !== 1,
				fileName: `snapshot.${(request.blob.type.split("/")[1] ?? "bin").split(";")[0]}`,
				fileType: request.blob.type || "application/octet-stream",
				category: "image",
				idempotencyId: request.idempotencyId,
				metadata: {
					...(request.metadata ?? {}),
					adminEndpointFamily: "uploads",
					adminOperation: request.operation,
					adminOrganizationId: request.organizationId,
					adminSnapshotId: request.snapshotId,
					adminParentId: request.parentId
				}
			});
		} catch (error) {
			// A row that was already admitted must remain reachable if bridge admission is
			// temporarily unavailable during a retry. A fresh admission failure has no durable
			// row (and therefore cannot be retried safely from this lane).
			let durableRow: AdminSmallFileUpload | undefined;
			try {
				durableRow = await this.getRecord(request.id);
			} catch {
				// Preserve the original admission error when the diagnostic lookup is also unavailable.
			}
			if (durableRow) this.scheduleAdmissionRetry(request.id, error);
			else this.reject(request.id, error);
			return result;
		}
		void this.drain().catch((error) => this.reject(request.id, error));
		return result;
	}

	async drain(): Promise<void> {
		if (this.destroyed) return;
		const ownership = await getAdminDurableOwnershipForCoordinator();
		if (!ownership) {
			const pending = await withIdbReopen(() => db.smallFileUploads.where("status").equals("pending").count());
			if (pending === 0 && ![...this.memoryRows.values()].some((row) => row.status === "pending")) return;
			await this.lane.drain();
			return;
		}
		await withSchedulerLease({
			ownership,
			originKey: ADMIN_SMALL_FILE_ORIGIN,
			ownerId: this.ownerId,
			ttlMs: ADMIN_SMALL_FILE_LEASE_TTL_MS,
			// R-0 (owner directive 2026-08-26): poisoned rows ARE scheduler work — paced scheduler
			// work. They are excluded from this count only while their current pacing window is open,
			// so the scheduler still goes idle instead of holding the lease across a fifteen-minute
			// defer, and the row is counted (and drained) again as soon as that window elapses. It
			// used to mean "parked, permanently not our problem", which is what left an exhausted
			// admin small-file row with no automatic path back.
			countPending: async () =>
				(await this.countDrainable(ownership)) + [...this.memoryRows.values()].filter((row) => row.status === "pending").length,
			// Nothing drainable means nothing to schedule; admin deliberately leaves the durable
			// work row untouched in that case.
			noteIdleWork: false,
			run: () => this.lane.drain()
		});
	}

	async findRetryableRotation(conversationId: string, snapshotId: string): Promise<AdminRetryableRotationIdentity | undefined> {
		const matchesRotation = (record: AdminSmallFileUpload) =>
			record.operation === "rotation" &&
			record.endpointFamily === "uploads" &&
			record.conversationId === conversationId &&
			record.snapshotId === snapshotId &&
			record.status !== "uploaded" &&
			typeof record.idempotencyId === "string" &&
			record.idempotencyId.length > 0;
		const candidates = [
			...(await db.smallFileUploads.where("operation").equals("rotation").and(matchesRotation).toArray()),
			...[...this.memoryRows.values()].filter(matchesRotation)
		];
		if (candidates.length > 1) {
			throw new Error("Multiple retryable Admin rotation uploads exist for the same snapshot");
		}
		const candidate = candidates[0];
		return candidate?.idempotencyId ? { id: candidate.id } : undefined;
	}

	private async countDrainable(ownership: Awaited<ReturnType<typeof getAdminDurableOwnershipForCoordinator>>): Promise<number> {
		if (!ownership) return 0;
		// Keys only: the poison admission needs nothing but the row id, and every pending row carries
		// a Blob payload that `toArray()` would structured-clone out of IndexedDB on each of the three
		// counts a drain performs.
		// A throttled/offline wake skips the earlier recovery scans, so this may be the
		// first database read. Recover here without retrying scheduler or writer work.
		const pendingIds = await withIdbReopen(() => db.smallFileUploads.where("status").equals("pending").primaryKeys());
		const now = Date.now();
		let count = 0;
		for (const id of pendingIds) {
			try {
				if (typeof ownership.readPoisonFailure !== "function") {
					count += 1;
					continue;
				}
				const poisonRecord = await ownership.readPoisonFailure(ADMIN_SMALL_FILE_ORIGIN, { itemId: String(id) });
				// R-0: an over-ceiling row is counted again the moment its pacing window elapses, so it
				// re-enters the scheduler on its own rather than waiting for a page reload.
				if (!poisonRecord || now >= computePoisonPacedUntil(poisonRecord, now)) count += 1;
			} catch (error) {
				console.warn("[Upload] Admin poison admission unavailable; deferring item", { id, error });
			}
		}
		return count;
	}

	async recoverAnomalousItem(
		id: string,
		audit: { requestedBy: "user" | "support"; reason: string; correlationId?: string }
	): Promise<SmallFileRecord> {
		return this.lane.recoverAnomalousItem(id, audit);
	}

	rearm(): void {
		let stage = "parked-row-scan";
		void this.requeueBudgetParkedRows()
			.catch((error) => console.warn("[Upload] Admin small-file R-0 requeue failed", this.recoveryFailureDetails(stage, error)))
			.then(() => {
				stage = "backoff-reset";
				return this.lane.accelerateBackoffForRecoveryWake();
			})
			.then(() => {
				stage = "drain";
				return this.drain();
			})
			.catch((error) => console.error("[Upload] Admin small-file rearm failed", this.recoveryFailureDetails(stage, error)));
	}

	private recoveryFailureDetails(stage: string, error: unknown): Record<string, unknown> {
		const failure = redactedUploadFailure(error);
		// Only fixed platform text can bypass the shared error-message redaction. Arbitrary
		// exceptions may reflect filenames, server bodies or signed URLs, even for IDB errors.
		const platformMessages = [
			"Database has been closed",
			"DatabaseClosedError Database has been closed",
			"Connection to Indexed Database server lost",
			"Transaction aborted",
			"Attempt to get a record without an in-progress transaction"
		];
		let errorMessage = failure.message;
		try {
			const candidateMessage = error instanceof Error ? error.message : undefined;
			if (typeof candidateMessage === "string" && platformMessages.includes(candidateMessage)) errorMessage = candidateMessage;
		} catch {
			// A throwing message getter must not hide the original recovery failure.
		}
		return {
			...uploadFailureLogDetails(error),
			stage,
			laneId: this.ownerId,
			databaseOpen: db.isOpen(),
			errorName: failure.name,
			errorMessage
		};
	}

	/**
	 * R-0 one-time startup requeue for rows already parked on this admin's device by the attempt-count
	 * rule the directive abolished. It runs once per page load, from `rearm`, before the first drain.
	 *
	 * Safety is the same argument the Live lane's pass makes: a requeued row re-enters the shared
	 * lane's reload path, which revalidates the payload against the durable target checkpoint and
	 * re-reads the receipt oracle before any PUT. So this cannot re-send bytes through an ambiguous
	 * receipt; a row that is genuinely evidence-terminal re-derives that verdict and re-parks. The
	 * two things the reload path cannot re-derive — missing local bytes and an already-recorded
	 * evidence terminal — are filtered here, with one warn per skipped row (root `CLAUDE.md`,
	 * "Diagnostic logging when fixing silent failures").
	 *
	 * The durable poison record is deliberately NOT cleared: the row returns to the pool on the paced
	 * tier, so a still-broken item cannot immediately hot-loop against its healthy siblings.
	 */
	async requeueBudgetParkedRows(): Promise<string[]> {
		if (this.r0RequeueDone) return [];
		this.r0RequeueDone = true;
		let parked: AdminSmallFileUpload[];
		try {
			parked = await withIdbReopen(() => db.smallFileUploads.where("status").equals("needs-attention").toArray(), {
				payloadScan: true
			});
		} catch (error) {
			console.warn(
				"[Upload] Admin small-file R-0 requeue could not read parked rows",
				this.recoveryFailureDetails("parked-row-scan", error)
			);
			this.r0RequeueDone = false;
			this.r0RequeueRetryTimer ??= globalThis.setTimeout(() => {
				this.r0RequeueRetryTimer = undefined;
				if (!this.destroyed) this.rearm();
			}, ADMIN_ADMISSION_RETRY_DELAY_MS);
			return [];
		}
		const requeued: string[] = [];
		for (const row of parked) {
			// Shared probe for the byte count (`size`, else an ArrayBuffer row's `byteLength`). Presence
			// stays admin's own two-field rule: this store keeps the Blob and its structured-clone
			// fallback in SEPARATE fields, so only "both fields absent" is positive proof of loss.
			const { payloadSize } = describeParkedPayload(row.payload ?? row.payloadBytes);
			const disposition = classifyParkedSmallFileRow({
				payloadSize,
				payloadPresent: row.payload === undefined && row.payloadBytes === undefined ? false : undefined,
				error: row.lastError,
				receiptCheckpointPresent: row.target !== undefined || row.completionReceipt !== undefined
			});
			if (disposition !== "requeue") {
				console.warn("[Upload] Admin small-file parked row was not requeued", {
					id: row.id,
					operation: row.operation,
					disposition,
					retryAttempt: row.retryAttempt
				});
				continue;
			}
			try {
				// CAS on the parked status: a concurrent tab may already have completed, replaced, or
				// re-parked this row since the read above.
				const updated = await db.smallFileUploads
					.where("id")
					.equals(row.id)
					.and((current) => current.status === "needs-attention")
					.modify({ status: "pending", retryAt: null });
				if (updated > 0) requeued.push(row.id);
				else console.warn("[Upload] Admin small-file parked row changed before requeue", { id: row.id });
			} catch (error) {
				console.warn("[Upload] Admin small-file parked row requeue failed", { id: row.id }, uploadFailureLogDetails(error));
			}
		}
		if (requeued.length > 0) console.info(`[Upload] Re-queued ${requeued.length} parked admin small-file upload(s) for retry`);
		return requeued;
	}

	destroy(): void {
		this.destroyed = true;
		if (this.admissionRetryTimer) globalThis.clearTimeout(this.admissionRetryTimer);
		if (this.r0RequeueRetryTimer) globalThis.clearTimeout(this.r0RequeueRetryTimer);
		this.admissionRetryTimer = undefined;
		this.r0RequeueRetryTimer = undefined;
		for (const id of [...this.admissionBudgets.keys()]) this.clearAdmissionBudget(id);
		for (const id of [...this.finalizingBudgets.keys()]) this.clearFinalizingBudget(id);
		this.lane.destroy();
		this.memoryRows.clear();
		this.completionResults.clear();
		this.rejectAll(new Error("Admin small-file upload lane was destroyed"));
		for (const id of this.writerTokens.keys()) void this.release(id);
	}

	private async readObjectProcessingReceipt(
		record: SmallFileRecord,
		checkpoint: SmallFileCompletionReceiptCheckpoint
	): Promise<SmallFileReceiptState> {
		if (!checkpoint.takeGeneration) throw uploadControlError("Processing receipt has no take identity");
		const control = readUploadKnownSizeControl(record);
		if (!control) throw uploadControlError("Processing receipt has no saved source");
		let snapshot;
		try {
			snapshot = await readKnownSizeUploadReceipt(
				this.api._uploadsFacade(),
				{ ...checkpoint, takeGeneration: checkpoint.takeGeneration },
				control
			);
		} catch (error) {
			if (isEvidenceTerminalFailure(error)) throw error;
			console.warn("[Upload] Admin object processing receipt is unreadable; keeping the saved source", {
				id: record.id,
				...uploadFailureLogDetails(error)
			});
			return "unknown";
		}
		if (snapshot.processingWork?.state === "held") {
			try {
				await this.api
					._uploadsFacade()
					.retryWork(checkpoint.registryId, { ...checkpoint, takeGeneration: checkpoint.takeGeneration });
			} catch (error) {
				if (isEvidenceTerminalFailure(error)) throw error;
				console.warn("[Upload] Admin processing recovery is unavailable; retaining the source", {
					id: record.id,
					...uploadFailureLogDetails(error)
				});
				return "unknown";
			}
			return "pending";
		}
		const observed = projectUploadObservation({ takenAt: new Date(), registry: snapshot });
		if (observed.transferVerified !== true) return "unknown";
		if (observed.outputReady) {
			if (record.organizationId) {
				try {
					const organization = await this.api.get?.(`/organizations/${encodeURIComponent(record.organizationId)}`);
					if (!organization || organization.id !== record.organizationId) return "unknown";
					this.completionResults.set(record.id, { organization });
				} catch (error) {
					console.warn("[Upload] Organization result is unreadable after verified upload; retaining source", {
						id: record.id,
						...uploadFailureLogDetails(error)
					});
					return "unknown";
				}
			}
			return "received";
		}
		if (observed.outputQuality === "failed")
			throw Object.assign(new Error("Snapshot processing failed after its bytes were accepted"), {
				code: "UPLOAD_PROCESSING_FAILED",
				status: 422
			});
		if (!this.finalizingBudgets.has(record.id)) this.armFinalizingWaiterBudget(record.id);
		return "pending";
	}

	private async presign(input: Parameters<SmallFileWire["presign"]>[0]): Promise<string | SmallFilePresignTarget> {
		const record = input.id ? await this.getRecord(input.id) : undefined;
		if (!record) throw new Error("Admin small-file durable row is missing");
		if (record.endpointFamily !== "uploads") {
			throw new Error("Admin small-file row is missing its uploads-family identity");
		}
		// The settling ceiling that used to wrap this call now lives in the shared lane, which
		// applies it around every `wire.presign` for every host. See the store hooks above.
		return this.presignRecord(record, input);
	}

	private async presignRecord(
		record: AdminSmallFileUpload,
		input: Parameters<SmallFileWire["presign"]>[0]
	): Promise<string | SmallFilePresignTarget> {
		const fileDetails = adminSmallFileDetails(record);
		if (typeof input.expectedBytes === "number" && input.expectedBytes >= DIRECT_PUT_MAX_BYTES_HINT) {
			console.info("[Upload] Admin small-file payload exceeds the direct-put ceiling; using multipart without probing", {
				id: record.id,
				conversationId: record.conversationId,
				expectedBytes: input.expectedBytes,
				directPutMaxBytesHint: DIRECT_PUT_MAX_BYTES_HINT
			});
			return this.uploadViaMultipartFallback(record, fileDetails, input);
		}
		const targetRequestAbortController = new AbortController();
		let response: unknown;
		try {
			response = await withTargetTimeout(
				this.createUploadTarget(
					record,
					{
						kind: "image",
						conversationId: record.conversationId,
						fileDetails: [fileDetails],
						type: constants.DOCUMENT_TYPE.IMAGE,
						idempotencyId: record.idempotencyId ?? record.id,
						expectedBytes: input.expectedBytes,
						supportedTransports: ["direct-put"]
					},
					targetRequestAbortController.signal
				),
				(error) => {
					console.warn("Snapshot upload target request timed out; aborting", {
						conversationId: record.conversationId,
						timeoutMs: SMALL_FILE_TARGET_TIMEOUT_MS
					});
					targetRequestAbortController.abort(error);
				}
			);
		} catch (error) {
			if (readUploadFacadeErrorCode(error) === "UPLOAD_TRANSPORT_UNSUPPORTED") {
				return this.uploadViaMultipartFallback(record, fileDetails, input);
			}
			throw error;
		}
		const readyReceipt = exactReadyReceipt(response, "direct-put");
		if (readyReceipt) {
			return { alreadyComplete: true, alreadyCompleteReceipt: readyReceipt };
		}
		const target = exactActiveTarget(response);
		if (target) return target;
		const mintedIdentity = exactAttemptIdentity(response, "direct-put");
		if (mintedIdentity) {
			this.abortAttemptInBackground(record, mintedIdentity, "direct-put", "Failed to abort rejected Admin small-file target");
		}
		throw new Error("Uploads facade returned an invalid Admin small-file target");
	}

	private async uploadViaMultipartFallback(
		record: AdminSmallFileUpload,
		fileDetails: Record<string, unknown>,
		input: Parameters<SmallFileWire["presign"]>[0]
	): Promise<SmallFilePresignTarget> {
		const payload = recoverAdminPayload(record);
		if (!payload) throw new Error("Admin multipart upload payload is missing or unreadable");
		if (exceedsMultipartPartLimit(payload.size)) {
			throw new Error(
				`Snapshot payload exceeds the multipart part limit (${payload.size} bytes; maximum ${maxUploadableBytes()} bytes)`
			);
		}
		if (!this.api.completeUploadsUpload) throw new Error("Admin multipart upload completion is unavailable");
		const totalChunks = requiredMultipartParts(payload.size);
		const targetRequestAbortController = new AbortController();
		const completionKey = record.idempotencyId ?? record.id;
		let response: unknown;
		try {
			response = await withTargetTimeout(
				this.createUploadTarget(
					record,
					{
						kind: "image",
						conversationId: record.conversationId,
						fileDetails: [fileDetails],
						type: constants.DOCUMENT_TYPE.IMAGE,
						idempotencyId: completionKey,
						expectedBytes: input.expectedBytes,
						supportedTransports: ["multipart"],
						totalChunks
					},
					targetRequestAbortController.signal
				),
				(error) => {
					console.warn("Snapshot multipart upload target request timed out; aborting", {
						conversationId: record.conversationId,
						timeoutMs: SMALL_FILE_TARGET_TIMEOUT_MS
					});
					targetRequestAbortController.abort(error);
				}
			);
		} catch (error) {
			throw this.destroyed ? error : normalizeActiveMultipartCancellation(error);
		}
		const readyReceipt = exactReadyReceipt(response, "multipart");
		if (readyReceipt) {
			return { alreadyComplete: true, alreadyCompleteReceipt: readyReceipt };
		}
		const target = exactMultipartActiveTargets(response, totalChunks);
		if (!target) {
			const mintedIdentity = exactAttemptIdentity(response, "multipart");
			if (mintedIdentity) {
				this.abortAttemptInBackground(record, mintedIdentity, "multipart", "Failed to abort rejected Admin multipart target");
			}
			throw new Error("Uploads facade returned an invalid Admin multipart target");
		}

		try {
			await this.assertMultipartRotationCurrent(record, target.identity);
			const parts: Array<{ targetId: string; ordinal: number; ETag: string; PartNumber: number }> = [];
			for (let index = 0; index < target.targets.length; index += 1) {
				const envelope = target.targets[index];
				const partNumber = index + 1;
				const partBlob = payload.slice(index * CHUNK_SIZE, Math.min(payload.size, partNumber * CHUNK_SIZE), "");
				const partResponse = await putToS3WithStallTimeout(partBlob, envelope.url, { headers: envelope.headers });
				if (!partResponse.ok) throw new Error(`Multipart part ${partNumber} upload failed with status ${partResponse.status}`);
				const ETag = partResponse.headers.get("ETag");
				if (!ETag) throw new Error(`Multipart part ${partNumber} upload did not return an ETag`);
				parts.push({ targetId: envelope.targetId, ordinal: envelope.ordinal, ETag, PartNumber: partNumber });
			}

			await this.assertMultipartRotationCurrent(record, target.identity);
			if (this.destroyed) throw new DOMException("Admin multipart upload was aborted", "AbortError");
			await this.api
				.completeUploadsUpload(target.identity.registryId, target.identity.attemptId, target.identity.generation, parts)
				.catch((error: unknown) => {
					throw tagUploadRefusalTrigger(error, "complete");
				});
			// A lane torn down while `complete` was in flight cannot checkpoint the receipt; the catch below
			// releases the minted attempt and the next lane re-mints (the server replays a finished attempt).
			if (this.destroyed) throw new DOMException("Admin multipart upload was aborted", "AbortError");
			// LSP-19929: once the server has acknowledged `complete`, every byte is durably on S3 and the
			// only remaining question is WHEN the finalizer flips the registry to `ready`. That question
			// belongs to the shared lane engine, exactly as it does for the direct-put path: the engine
			// checkpoints this receipt on the durable row (`checkpointCompletionReceipt`), re-reads it
			// through `readReceipt` on its settling cadence under the durable settling ceiling, and never
			// re-presigns or re-PUTs. Waiting here instead (the original in-attempt poll loop) turned a slow
			// finalizer, one transient GET failure, or a lane teardown into "Failed to rotate snapshot."
			// even though the upload had already succeeded. The dialog's own wait is bounded by
			// `ADMIN_FINALIZING_WAITER_BUDGET_MS`, armed when the receipt is checkpointed.
			console.info("[Upload] Admin multipart rotation completed; handing the completion receipt to the lane", {
				id: record.id,
				conversationId: record.conversationId,
				registryId: target.identity.registryId,
				attemptId: target.identity.attemptId,
				generation: target.identity.generation,
				parts: parts.length
			});
			return { alreadyComplete: true, alreadyCompleteReceipt: target.identity };
		} catch (error) {
			// The facade abort is terminal: it cancels the H-1 registry owner and tombstones the
			// matching stub. Transfer, completion, and receipt failures must preserve that owner because
			// this durable row retries with the same idempotencyId and the server rotates its active attempt.
			const errorCode = readUploadFacadeErrorCode(error);
			if (errorCode === "UPLOAD_ROTATION_SUPERSEDED" || this.destroyed) {
				this.abortAttemptInBackground(record, target.identity, "multipart", "Failed to abort stopped Admin multipart upload");
			} else {
				this.abortAttemptInBackground(
					record,
					target.identity,
					"multipart",
					"Failed to release failed Admin multipart upload bytes",
					true
				);
			}
			throw this.destroyed ? error : normalizeActiveMultipartCancellation(error);
		}
	}

	private async createUploadTarget(record: AdminSmallFileUpload, body: Record<string, unknown>, signal: AbortSignal): Promise<unknown> {
		// Capture before admission: cleanup may settle and remove itself after the API observes the
		// still-active target but before this client receives the typed conflict.
		const isRotationTarget = this.rotationTargetKey(record) !== undefined;
		const relatedCleanupAtAdmission = this.snapshotRotationAbortCleanup(record);
		try {
			return await this.api.createUploadsUpload(body, signal);
		} catch (error) {
			if (readUploadFacadeErrorCode(error) !== "REPLACEMENT_IN_PROGRESS") throw error;
			if (!isRotationTarget) {
				this.warnTargetAdmissionStopped(record, "non-rotation-replacement-conflict", false, 0);
				throw error;
			}
			const waitStartedAt = Date.now();
			if (!relatedCleanupAtAdmission) {
				// The superseded writer may not have reached its next checkpoint yet. Give it one bounded
				// window to register cleanup before classifying the newest rotation as terminal.
				await new Promise((resolve) => globalThis.setTimeout(resolve, ADMIN_ADMISSION_RETRY_DELAY_MS));
				const cleanupInFlight = this.hasRotationAbortCleanup(record);
				const waitedMs = Date.now() - waitStartedAt;
				this.assertTargetAdmissionActive(record, signal, cleanupInFlight, waitedMs);
				// A cleanup that is still tracked has not genuinely settled. Yield this attempt back to the
				// durable retry lane instead of burning the one conflict retry while the abort is in flight.
				if (cleanupInFlight) {
					this.warnTargetAdmissionStopped(record, "rotation-cleanup-still-pending", true, waitedMs);
					throw abortCleanupStillPendingError();
				}
			} else {
				const cleanupSettled = await this.cleanupSettledWithinAdmissionWindow(relatedCleanupAtAdmission);
				const cleanupInFlight = this.hasRotationAbortCleanup(record);
				const waitedMs = Date.now() - waitStartedAt;
				this.assertTargetAdmissionActive(record, signal, cleanupInFlight, waitedMs);
				// A second cleanup may register after the admission snapshot. Do not spend the
				// bounded retry while any current same-target cleanup still owns the gate.
				if (!cleanupSettled || cleanupInFlight) {
					this.warnTargetAdmissionStopped(record, "rotation-cleanup-still-pending", cleanupInFlight, waitedMs);
					throw abortCleanupStillPendingError();
				}
			}
			// One bounded retry only. A repeated conflict is authoritative evidence that another
			// owner still holds the target, so it must retain the shared terminal classification.
			try {
				return await this.api.createUploadsUpload(body, signal);
			} catch (retryError) {
				if (readUploadFacadeErrorCode(retryError) === "REPLACEMENT_IN_PROGRESS") {
					this.warnTargetAdmissionStopped(
						record,
						"replacement-conflict-after-bounded-retry",
						this.hasRotationAbortCleanup(record),
						Date.now() - waitStartedAt
					);
				}
				throw retryError;
			}
		}
	}

	private assertTargetAdmissionActive(
		record: AdminSmallFileUpload,
		signal: AbortSignal,
		cleanupInFlight: boolean,
		waitedMs: number
	): void {
		if (this.destroyed) {
			this.warnTargetAdmissionStopped(record, "lane-destroyed", cleanupInFlight, waitedMs);
			throw new DOMException("Admin small-file upload target admission was aborted", "AbortError");
		}
		if (signal.aborted) {
			this.warnTargetAdmissionStopped(record, "request-aborted", cleanupInFlight, waitedMs);
			throw new DOMException("Admin small-file upload target admission was aborted", "AbortError");
		}
	}

	private warnTargetAdmissionStopped(record: AdminSmallFileUpload, condition: string, cleanupInFlight: boolean, waitMs: number): void {
		console.warn("[Upload] Admin small-file target admission stopped", {
			condition,
			recordId: record.id,
			conversationId: record.conversationId,
			operation: record.operation,
			snapshotId: record.snapshotId,
			cleanupInFlight,
			waitMs
		});
	}

	private async cleanupSettledWithinAdmissionWindow(cleanup: Promise<void>): Promise<boolean> {
		let timeout: ReturnType<typeof setTimeout> | undefined;
		const observationExpired = new Promise<false>((resolve) => {
			timeout = globalThis.setTimeout(() => resolve(false), ADMIN_ADMISSION_RETRY_DELAY_MS);
		});
		const settled = cleanup.then(() => true as const);
		const outcome = await Promise.race([settled, observationExpired]);
		if (timeout !== undefined) globalThis.clearTimeout(timeout);
		return outcome;
	}

	private abortAttemptInBackground(
		record: AdminSmallFileUpload,
		identity: SmallFileCompletionReceiptCheckpoint,
		transport: AdminSmallFileTransport,
		warningMessage: string,
		preserveOwner = false
	): void {
		const abortOperation = Promise.resolve().then(() =>
			preserveOwner
				? this.api.abortUploadsUpload(
						identity.registryId,
						identity.attemptId,
						identity.generation,
						transport,
						identity.takeGeneration,
						true
					)
				: identity.takeGeneration
					? this.api.abortUploadsUpload(
							identity.registryId,
							identity.attemptId,
							identity.generation,
							transport,
							identity.takeGeneration
						)
					: this.api.abortUploadsUpload(identity.registryId, identity.attemptId, identity.generation, transport)
		);
		const cleanup = abortOperation
			.then(
				() => {
					console.info("Admin upload cleanup completed", { registryId: identity.registryId, transport });
				},
				(error) => {
					console.warn(warningMessage, {
						registryId: identity.registryId,
						...uploadFailureLogDetails(error)
					});
				}
			)
			.catch(() => undefined);
		const trackedCleanup = withTargetTimeout(
			cleanup,
			() => undefined,
			`Admin ${transport} upload abort timed out after ${SMALL_FILE_TARGET_TIMEOUT_MS}ms`
		)
			.catch((error) => {
				console.warn(`[Upload] ${warningMessage}`, {
					registryId: identity.registryId,
					...uploadFailureLogDetails(error)
				});
			})
			.catch(() => undefined);
		this.trackRotationAbortCleanup(record, trackedCleanup);
	}

	private rotationTargetKey(record: AdminSmallFileUpload): string | undefined {
		if (record.operation !== "rotation" || !record.snapshotId) return undefined;
		return JSON.stringify([record.conversationId, record.snapshotId]);
	}

	private trackRotationAbortCleanup(record: AdminSmallFileUpload, cleanup: Promise<void>): void {
		const targetKey = this.rotationTargetKey(record);
		if (!targetKey) return;
		const cleanups = this.abortCleanupsByRotationTarget.get(targetKey) ?? new Set<Promise<void>>();
		cleanups.add(cleanup);
		this.abortCleanupsByRotationTarget.set(targetKey, cleanups);
		void cleanup.then(() => {
			cleanups.delete(cleanup);
			if (cleanups.size === 0) this.abortCleanupsByRotationTarget.delete(targetKey);
		});
	}

	private snapshotRotationAbortCleanup(record: AdminSmallFileUpload): Promise<void> | undefined {
		const targetKey = this.rotationTargetKey(record);
		if (!targetKey) return undefined;
		const cleanups = this.abortCleanupsByRotationTarget.get(targetKey);
		if (!cleanups?.size) return undefined;
		return Promise.allSettled([...cleanups]).then(() => undefined);
	}

	private hasRotationAbortCleanup(record: AdminSmallFileUpload): boolean {
		const targetKey = this.rotationTargetKey(record);
		return targetKey !== undefined && Boolean(this.abortCleanupsByRotationTarget.get(targetKey)?.size);
	}

	private async assertMultipartRotationCurrent(
		record: AdminSmallFileUpload,
		identity: SmallFileCompletionReceiptCheckpoint
	): Promise<void> {
		if (record.operation !== "rotation") return;
		const current = await this.getRecord(record.id);
		if (
			current?.operation === "rotation" &&
			current.conversationId === record.conversationId &&
			current.snapshotId === record.snapshotId &&
			current.idempotencyId === record.idempotencyId
		) {
			return;
		}
		console.info("Admin multipart rotation was superseded before completion", {
			id: record.id,
			registryId: identity.registryId,
			expectedIdempotencyId: record.idempotencyId,
			currentIdempotencyId: current?.idempotencyId
		});
		throw Object.assign(new Error("Admin multipart rotation was superseded by a newer attempt"), {
			code: "UPLOAD_ROTATION_SUPERSEDED",
			status: 409
		});
	}

	/**
	 * The single receipt oracle for both checkpoint flavours. Admin leaves the shared lane's
	 * `receiptSealWaitMs` at its 0 default — `enqueueAndWait` blocks an interactive dialog, so a
	 * multi-second in-attempt seal wait would be user-visible; a `pending` server is re-read by the
	 * lane's ordinary retry schedule instead.
	 */
	private async readReceipt(
		expected: SmallFileCompletionReceiptCheckpoint,
		kind: SmallFileReceiptQuery["kind"]
	): Promise<SmallFileReceiptState> {
		let current: unknown;
		try {
			current = await this.api.getUploadsUpload(expected.registryId);
		} catch (error) {
			// A definitive 404 proves the server holds no such registry identity. For a direct-put TARGET
			// checkpoint the bytes were never accepted and the lane's reload path re-presigns and re-PUTs
			// them; for a COMPLETION receipt (bytes the server already acknowledged) the engine parks the
			// row after `completionReceiptAbsentStrikes` consecutive reads. Any other read failure proves
			// nothing and must retain the bytes.
			if (!isRegistryNotFoundError(error)) {
				// Silent-failure rule: an `unknown` verdict retries on the charged, minutes-capped cadence (R-0),
				// so the timeline must show WHY each re-read proved nothing.
				console.warn("[Upload] Admin small-file receipt read failed; the verdict is unknown and the bytes are retained", {
					registryId: expected.registryId,
					attemptId: expected.attemptId,
					generation: expected.generation,
					kind,
					...uploadFailureLogDetails(error)
				});
				return "unknown";
			}
			// Silent-failure rule: name the 404 here so the Datadog timeline shows which read produced it.
			console.warn("[Upload] Admin small-file receipt read: server holds no registry identity for this checkpoint", {
				registryId: expected.registryId,
				attemptId: expected.attemptId,
				generation: expected.generation
			});
			return "absent";
		}
		// Admin's own `ready` predicate stays the authority for success; the shared classifier only
		// splits the remainder into "still finalizing" and "not authoritative".
		if (isExactCurrentReadyReceipt(current, expected)) return "received";
		const state = classifyAdminSmallFileReceipt(current, expected);
		if (state === "pending") return "pending";
		// LSP-19929: for a COMPLETION receipt (bytes the server already acknowledged), the server
		// reporting THIS exact attempt in a status that is neither finalizing nor ready (`failed`,
		// `abandoned`, ...) is as definitive as a 404 — its finalizer will never flip it to ready — so it
		// takes the same strike path instead of being re-read as "unknown" forever. A direct-put TARGET
		// read must not take it: the engine treats a target `absent` as "bytes never accepted" and
		// re-PUTs them, which would rewrite an attempt the server has already failed or abandoned. That
		// read stays `unknown` (retryable, bytes retained) exactly as before this change.
		if (kind === "completion" && isExactAttemptTerminalReceipt(current, expected)) {
			console.warn("[Upload] Admin small-file receipt read: server reports this completed attempt in a terminal status", {
				registryId: expected.registryId,
				attemptId: expected.attemptId,
				generation: expected.generation,
				status: (current as { status?: unknown }).status
			});
			return "absent";
		}
		if (kind === "completion" && isSupersededAttemptReceipt(current, expected)) {
			const projected = current as { attempt?: { id?: unknown; generation?: unknown }; status?: unknown };
			console.warn("[Upload] Admin small-file receipt read: server holds this registry under a different attempt identity", {
				registryId: expected.registryId,
				attemptId: expected.attemptId,
				generation: expected.generation,
				currentAttemptId: projected.attempt?.id,
				currentGeneration: projected.attempt?.generation,
				status: projected.status
			});
			return "absent";
		}
		const projected = current as { attempt?: { id?: unknown; generation?: unknown }; status?: unknown } | null;
		console.warn("[Upload] Admin small-file receipt read is not authoritative for this checkpoint; the verdict is unknown", {
			registryId: expected.registryId,
			attemptId: expected.attemptId,
			generation: expected.generation,
			kind,
			currentAttemptId: projected?.attempt?.id,
			currentGeneration: projected?.attempt?.generation,
			status: projected?.status
		});
		return "unknown";
	}

	private async readObjectExists(expected: SmallFileCompletionReceiptCheckpoint): Promise<boolean | undefined> {
		if (!this.api.getUploadsObjectStatus) return undefined;
		try {
			return await this.api.getUploadsObjectStatus(expected.registryId, expected.attemptId, expected.generation);
		} catch (error) {
			if (isUploadTerminalFailure(error)) throw tagUploadRefusalTrigger(error, "objectStatus");
			console.warn("Admin small-file object status read failed", {
				registryId: expected.registryId,
				...uploadFailureLogDetails(error)
			});
			return undefined;
		}
	}

	private async claim(record: SmallFileRecord): Promise<SmallFileUploadClaim | undefined> {
		try {
			await ensureAdminUploadBridgeReady();
			const ownership = await getAdminDurableOwnershipForCoordinator();
			if (!ownership) return { isCurrent: async () => !this.destroyed, release: async () => undefined };
			const token = await ownership.acquireWriter({
				originKey: ADMIN_SMALL_FILE_ORIGIN,
				identity: { itemId: record.id },
				ownerId: this.ownerId,
				ttlMs: ADMIN_SMALL_FILE_LEASE_TTL_MS
			});
			if (!token) {
				this.scheduleAdmissionRetry(
					record.id,
					Object.assign(new Error("Admin small-file writer is currently owned by another tab"), {
						code: "UPLOAD_WRITER_UNAVAILABLE",
						retryable: true
					})
				);
				return undefined;
			}
			this.writerTokens.set(record.id, token);
			return {
				isCurrent: async () => ownership.ownsWriter(this.writerTokens.get(record.id) ?? token),
				renew: async () => {
					const renewed = await ownership.renewWriter({
						token: this.writerTokens.get(record.id) ?? token,
						ttlMs: ADMIN_SMALL_FILE_LEASE_TTL_MS
					});
					if (renewed) this.writerTokens.set(record.id, renewed);
					return !!renewed;
				},
				commitIfWriterOwns: async (effect) => {
					const currentOwnership = await getAdminDurableOwnershipForCoordinator();
					const currentToken = this.writerTokens.get(record.id) ?? token;
					if (!currentOwnership) return { kind: "applied", value: await effect() };
					const outcome = await currentOwnership.commitIfWriterOwns({
						token: currentToken,
						storeNames: ["smallFileUploads"],
						effect
					});
					return outcome;
				},
				release: () => this.release(record.id)
			};
		} catch (error) {
			console.warn("[Upload] Admin small-file writer claim unavailable", uploadFailureLogDetails(error));
			this.scheduleAdmissionRetry(record.id, error);
			return undefined;
		}
	}

	private async release(id: string): Promise<void> {
		const token = this.writerTokens.get(id);
		this.writerTokens.delete(id);
		if (!token) return;
		try {
			const ownership = await getAdminDurableOwnershipForCoordinator();
			if (ownership) await ownership.releaseWriter(token);
		} catch (error) {
			console.warn("[Upload] Admin small-file writer release failed", uploadFailureLogDetails(error));
		}
	}

	private async getRecord(id: string): Promise<AdminSmallFileUpload | undefined> {
		return this.memoryRows.get(id) ?? db.smallFileUploads.get(id);
	}

	private async updateRecord(id: string, changes: Partial<AdminSmallFileUpload>): Promise<number> {
		const retained = this.memoryRows.get(id);
		if (!retained) return db.smallFileUploads.update(id, changes);
		this.memoryRows.set(id, {
			...retained,
			...changes,
			...(changes.status === "uploaded" ? { payload: undefined, payloadBytes: undefined } : {})
		});
		return 1;
	}

	private async putRecord(record: AdminSmallFileUpload): Promise<void> {
		if (this.memoryRows.has(record.id)) {
			this.memoryRows.set(record.id, record);
			return;
		}
		try {
			await db.smallFileUploads.put(record);
		} catch (error) {
			const name = error instanceof Error ? error.name : undefined;
			// Never replace an existing durable attempt with a page-only copy.
			const storageRejected = readIdbErrorEvidence(error)?.some(
				(candidate) => candidate.name === "UnknownError" || candidate.name === "QuotaExceededError"
			);
			const fallback = !this.destroyed && storageRejected && !(await db.smallFileUploads.get(record.id));
			logError("Admin small-file persistence failed; upload may continue from memory", redactedUploadFailure(error), {
				id: record.id,
				errorName: name,
				memoryFallback: fallback
			});
			if (!fallback) throw error;
			this.memoryRows.set(record.id, record);
		}
	}

	private async commit<T>(id: string, effect: () => Promise<T>): Promise<T | false> {
		await ensureAdminUploadBridgeReady();
		const ownership = await getAdminDurableOwnershipForCoordinator();
		const token = this.writerTokens.get(id);
		if (ownership && token) {
			const outcome = await ownership.commitIfWriterOwns({ token, storeNames: ["smallFileUploads"], effect });
			return outcome.kind === "applied" ? outcome.value : false;
		}
		return effect();
	}

	private scheduleAdmissionRetry(id: string, error: unknown): void {
		if (this.destroyed) return;
		// The durable row survives a retryable admission miss and is drained again below, so the
		// waiter settles on that retry's outcome (`markUploaded` -> `resolve`, a terminal upload
		// failure -> `reject`) rather than on the miss. Snapshot/annotation/rotation callers turn a
		// rejection into "your snapshot failed to upload", which would be a lie while the same row is
		// still on its way to success.
		this.parkWaiterForAdmissionRetry(id, error);
		if (this.admissionRetryTimer) return;
		this.admissionRetryTimer = globalThis.setTimeout(() => {
			this.admissionRetryTimer = undefined;
			if (this.destroyed) return;
			void this.drain().catch((retryError) => this.rejectAll(retryError));
		}, ADMIN_ADMISSION_RETRY_DELAY_MS);
	}

	/**
	 * Parks the item's waiters until the scheduled admission retry produces an outcome, under a hard
	 * deadline. The deadline is armed once per parked item and is never extended by a later miss, so
	 * the wait stays bounded even if the retry drain stops calling back entirely (a poisoned or
	 * deleted row can make a drain a no-op). The most recent admission error is what the caller
	 * finally sees, so the message still describes why admission never happened.
	 */
	private parkWaiterForAdmissionRetry(id: string, error: unknown): void {
		if (!this.waiters.has(id)) return;
		const existing = this.admissionBudgets.get(id);
		if (existing) {
			existing.error = error;
			return;
		}
		const timer = globalThis.setTimeout(() => {
			// Report the most recent admission miss, not the one that armed the deadline.
			const latest = this.admissionBudgets.get(id)?.error ?? error;
			this.admissionBudgets.delete(id);
			this.reject(id, latest);
		}, ADMIN_ADMISSION_WAITER_BUDGET_MS);
		this.admissionBudgets.set(id, { timer, error });
	}

	/**
	 * LSP-19929: the engine owns the readiness wait for a handed-off completion receipt and never
	 * rejects a receipt-pending error to the waiter. This budget is what stops the rotation dialog from
	 * spinning forever when the finalizer stalls or the receipt keeps reading `unknown`: after it elapses
	 * the waiter is rejected while the durable row keeps re-reading in the background. Armed on every
	 * completion-receipt checkpoint, so a retry that hands off a fresh attempt gets a fresh budget;
	 * cleared by resolve/reject/destroy.
	 */
	private armFinalizingWaiterBudget(id: string): void {
		if (!this.waiters.has(id)) return;
		this.clearFinalizingBudget(id);
		const timer = globalThis.setTimeout(() => {
			this.finalizingBudgets.delete(id);
			if (!this.waiters.has(id)) return;
			console.warn("[Upload] Admin small-file completion receipt still not ready after the waiter budget; releasing the dialog", {
				id,
				budgetMs: ADMIN_FINALIZING_WAITER_BUDGET_MS
			});
			this.reject(
				id,
				Object.assign(new Error("Snapshot upload is still finalizing on the server; it keeps retrying in the background"), {
					code: "UPLOAD_RECEIPT_PENDING_TIMEOUT"
				})
			);
		}, ADMIN_FINALIZING_WAITER_BUDGET_MS);
		this.finalizingBudgets.set(id, timer);
	}

	private clearFinalizingBudget(id: string): void {
		const timer = this.finalizingBudgets.get(id);
		if (timer === undefined) return;
		this.finalizingBudgets.delete(id);
		globalThis.clearTimeout(timer);
	}

	private clearAdmissionBudget(id: string): void {
		const budget = this.admissionBudgets.get(id);
		if (!budget) return;
		this.admissionBudgets.delete(id);
		globalThis.clearTimeout(budget.timer);
	}

	private resolve(id: string): void {
		this.clearAdmissionBudget(id);
		this.clearFinalizingBudget(id);
		const pending = this.waiters.get(id);
		const result = this.completionResults.get(id) ?? {};
		this.completionResults.delete(id);
		if (!pending) return;
		this.waiters.delete(id);
		for (const waiter of pending) waiter.resolve(result);
	}

	// A parked waiter is released by the budget deadline, by an abort/destroy, or by a terminal
	// upload outcome — never by the retryable admission miss that parked it.
	private reject(id: string, error: unknown): void {
		this.clearAdmissionBudget(id);
		this.clearFinalizingBudget(id);
		const pending = this.waiters.get(id);
		if (!pending) return;
		this.waiters.delete(id);
		for (const waiter of pending) waiter.reject(error);
	}

	private rejectAll(error: unknown): void {
		for (const id of this.waiters.keys()) this.reject(id, error);
	}
}

let singleton: AdminSmallFileUploadLane | undefined;
let testFetch: BlobPutFetch | undefined;

export function getAdminSmallFileUploadLane(api: AdminSmallFileApi): AdminSmallFileUploadLane {
	return (singleton ??= new AdminSmallFileUploadLane(api));
}

export function resetAdminSmallFileUploadLaneForTests(fetcher?: BlobPutFetch): void {
	singleton?.destroy();
	singleton = undefined;
	testFetch = fetcher;
}
